<script lang="ts">
	import { Nav, Footer } from '$lib/components/sections';
	import ScannerPicks from '$lib/components/workbench/ScannerPicks.svelte';
	import { Button, CompareSlider, MonoLabel, Readout, Rule, Section } from '$lib/components/ui';
	import Meta from '$lib/seo/Meta.svelte';

	const description =
		'How a 3D vehicle scan becomes a plotter-ready PPF cutting pattern: stretch checks, wrinkle prediction, measured sensor cutouts and a physical test fit.';

	const steps = [
		{
			index: '01',
			title: 'Capture the panel surface',
			body: 'Scan the actual vehicle panel with a structured-light or laser scanner. Export a surface mesh as STL, OBJ or PLY. The mesh is the measured starting point, not the final cut path.'
		},
		{
			index: '02',
			title: 'Clean only what affects the pattern',
			body: 'Remove floating geometry, fill the gaps that matter and reduce scan noise, without sanding away the character lines, openings and panel edges that decide the fit.'
		},
		{
			index: '03',
			title: 'Define coverage and seams',
			body: 'Choose the protected region on the real surface. Place seams where the installer can hide or manage them, then set edge wraps and reliefs before flattening.'
		},
		{
			index: '04',
			title: 'Flatten the curved mesh',
			body: 'Convert the selected 3D surface into a two-dimensional mesh. A useful flatten does more than lie flat: it reports stretch, compression and shear face by face, so the installer can judge how the film will behave before anything is cut.'
		},
		{
			index: '05',
			title: 'Check the pattern against the film',
			body: 'Run the numbers on the actual cut shape. Where does the film have to stretch past its working limit? Where is there more film than surface, and can that excess be worked out to a free edge or will it fold? A wrinkle caught on screen costs a seam or a relief cut. One discovered on the car costs the piece.'
		},
		{
			index: '06',
			title: 'Edit, add cutouts and export',
			body: 'Adjust anchors, seam positions and edge offsets. Round features are measured, not traced: a parking sensor is detected from its bezel ring on the scan, confirmed by hand and cut with a margin the installer sets. Then export SVG, DXF or PLT for the plotter, with the scan and every decision kept editable.'
		},
		{
			index: '07',
			title: 'Test fit before production',
			body: 'Cut cheap test material or a sacrificial film piece, check the alignment on the vehicle and feed corrections back into the digital pattern. Scanning takes out the guesswork, but it does not replace checking the fit on the car.'
		}
	] as const;

	const distinctions = [
		{
			title: 'A scan is not a template',
			body: 'A mesh describes a curved surface. A production pattern also needs coverage boundaries, seams, reliefs, wraps and a cut path the plotter can follow.'
		},
		{
			title: 'Flattening is not tracing',
			body: 'Tracing a photograph ignores compound curvature. Mesh flattening starts from measured surface geometry and makes distortion visible before film is cut.'
		},
		{
			title: 'The installer stays in control',
			body: 'Software can calculate and flag. The installer decides where a seam belongs, how far an edge wraps and whether the result is workable on the car.'
		},
		{
			title: 'A draft is not a production file',
			body: 'Sometimes a measurement falls short: a sensor ring that never fully closed, a flatten that fails its stretch check. The pattern still exports, but it goes out marked as a draft with the evidence attached. The gap is recorded, not hidden.'
		}
	] as const;
</script>

<Meta title="3D scan to PPF pattern workflow" {description} />

<Nav tone="paper" />
<main>
	<Section as="article" class="article">
		<header class="hero-copy">
			<MonoLabel>Technical guide · PPF pattern making</MonoLabel>
			<h1>How a 3D vehicle scan becomes a PPF cutting pattern</h1>
			<p class="lede">
				The practical workflow is scan → clean → define coverage → flatten → inspect → cut → test
				fit. The hard part is not making a mesh lie flat. It is keeping the installer’s intent
				intact while showing where flat film has to stretch across a curved panel.
			</p>
			<div class="hero-actions">
				<Button href="/#process" size="lg">See the product workflow</Button>
				<Button href="/pricing" variant="ghost" size="lg">View presale pricing</Button>
			</div>
		</header>

		<div class="evidence" aria-label="Workflow evidence">
			<Readout label="Input" value="STL · OBJ · PLY" />
			<Readout label="Output" value="SVG · DXF · PLT" />
			<Readout label="Required check" value="physical test fit" accent />
		</div>

		<figure class="comparison">
			<CompareSlider
				beforeSrc="/img/real-scan.webp"
				afterSrc="/img/real-template.webp"
				alt="The same Volvo FH trim shown first as a curved 3D scan and then as its flat PPF cutting template."
				width={1919}
				height={715}
				beforeLabel="3D scan"
				afterLabel="2D cutting template"
			/>
			<figcaption>
				One real Volvo FH trim from the studio: measured surface on the left, editable cut geometry
				on the right. Drag the seam to compare.
			</figcaption>
		</figure>

		<Rule crop />

		<section class="workflow" aria-labelledby="workflow-heading">
			<div class="section-intro">
				<MonoLabel>Seven decisions, not one conversion</MonoLabel>
				<h2 id="workflow-heading">The scan-to-pattern workflow</h2>
				<p>
					Each stage protects a different kind of accuracy: measurement, surface quality,
					installation intent, the film’s stretch budget, plotter geometry and real-world fit.
				</p>
			</div>
			<ol class="steps">
				{#each steps as step (step.index)}
					<li>
						<span class="step-index">{step.index}</span>
						<div>
							<h3>{step.title}</h3>
							<p>{step.body}</p>
						</div>
					</li>
				{/each}
			</ol>
		</section>

		<section class="fieldnote" aria-labelledby="fieldnote-heading">
			<div class="section-intro">
				<MonoLabel>One job, mid-workflow</MonoLabel>
				<h2 id="fieldnote-heading">What “measured” looks like on screen</h2>
			</div>
			<div class="fieldnote-copy">
				<p>
					This is the flatten step on a Range Rover rear-bumper scan: two million faces, processed
					locally. The detector has found a parking sensor and fitted a circle to its bezel. It
					measured Ø 26.5 mm, a fully closed ring and 0.12 mm RMS across 143 crest samples. Nothing
					is cut on trust. The installer clicks the glowing ring to confirm the sensor, then drags
					the dashed ring to set the cut margin with a live readout in millimetres. Here it sits at
					2 mm.
				</p>
				<p>
					The pre-flight column sits next to the export button on purpose. Crossed lines, short
					segments, interior stretch and the film’s stretch budget are all checked before a cut file
					ships. The hole itself follows the sensor’s true developed shape after flattening, not an
					idealised circle traced by eye.
				</p>
			</div>
		</section>

		<figure class="screenshot">
			<img
				src="/img/real-sensors.webp"
				alt="PPF Engine's flatten step: a detected parking-sensor ring on a Range Rover rear-bumper scan, confirmed at Ø 26.5 mm with a dashed 2 mm margin ring, pre-flight checks in the right panel."
				width={1512}
				height={789}
				loading="lazy"
				decoding="async"
			/>
			<figcaption>
				A confirmed sensor mid-drag: the solid ring is the measured bezel, the dashed ring is the
				actual cut. The panel card keeps the evidence next to the cutout it produced: bezel profile,
				ring closure, crest height.
			</figcaption>
		</figure>

		<div class="shot-readouts" aria-label="Figures from this job">
			<Readout label="Sensor" value="Ø 26.5 mm · ring closed" accent />
			<Readout label="Cut margin" value="2.0 mm · set by drag" />
			<Readout label="Scan" value="2,000,000 faces · local" />
		</div>

		<section class="distinctions" aria-labelledby="distinctions-heading">
			<div class="section-intro">
				<MonoLabel>What the terms mean</MonoLabel>
				<h2 id="distinctions-heading">
					Scanning, flattening and pattern making are different jobs
				</h2>
			</div>
			<div class="cards">
				{#each distinctions as item (item.title)}
					<article>
						<h3>{item.title}</h3>
						<p>{item.body}</p>
					</article>
				{/each}
			</div>
		</section>

		<section class="answer" aria-labelledby="equipment-heading">
			<div>
				<MonoLabel>Equipment</MonoLabel>
				<h2 id="equipment-heading">What scanner do PPF installers need?</h2>
			</div>
			<div class="answer-copy">
				<p>
					Any scanner that exports a usable mesh can enter the workflow. An entry-level
					structured-light handheld is enough to prove the process works on your own panels.
					Higher-end structured-light and laser scanners generally make cleanup faster and hold on
					to small features more reliably.
				</p>
				<p>
					Choose based on the panels you actually pattern, the accuracy you need, capture speed and
					how much mesh cleanup you are willing to do, not on a software badge. PPF Engine works
					with whichever scanner you pick, and the patterns you make are yours either way.
				</p>
				<p>
					New to pattern making? <a href="/guides/how-to-make-custom-ppf-patterns"
						>How to make custom PPF patterns</a
					> compares the scan route with tape lifts, photo tracing and editing a library pattern.
				</p>
				<p>
					See the <a href="/technology/scanner-plotter-compatibility"
						>scanner and plotter compatibility guide</a
					>
					for what we have tested ourselves, and
					<a href="/compare/ppf-engine-vs-exactflat">PPF Engine vs ExactFlat</a> if you are weighing a
					CAD-based flattening tool.
				</p>
			</div>
			<div class="shelf">
				<ScannerPicks placement="scan-guide" />
			</div>
		</section>

		<section class="closing" aria-labelledby="closing-heading">
			<MonoLabel>For custom and missing patterns</MonoLabel>
			<h2 id="closing-heading">Build the pattern while the vehicle is in your shop</h2>
			<p>
				PPF Engine is desktop pattern software for installation shops and independent installers.
				Your scans and patterns stay on your machine.
			</p>
			<div class="hero-actions">
				<Button href="/#pricing" size="lg">Join the presale</Button>
				<Button href="/faq" variant="ghost" size="lg">Read the FAQ</Button>
			</div>
		</section>
	</Section>
</main>
<Footer />

<style>
	:global(.article) {
		padding-top: 4.5rem;
	}
	.hero-copy {
		max-width: 62rem;
	}
	h1 {
		max-width: 15ch;
		margin-top: 1rem;
		font-size: var(--text-display);
		line-height: var(--text-display--line-height);
		letter-spacing: var(--text-display--letter-spacing);
		font-weight: var(--text-display--font-weight);
	}
	.lede {
		max-width: var(--container-measure);
		margin-top: 2rem;
		font-size: var(--text-lead);
		line-height: var(--text-lead--line-height);
		color: var(--color-ink-700);
	}
	.hero-actions {
		display: flex;
		flex-wrap: wrap;
		gap: 0.75rem;
		margin-top: 2rem;
	}
	.evidence {
		display: grid;
		grid-template-columns: repeat(3, minmax(0, 1fr));
		gap: 1px;
		margin-top: 4rem;
		border: 1px solid var(--color-line-strong);
		background: var(--color-line-strong);
	}
	.evidence :global(.ro) {
		min-height: 6rem;
		padding: 1rem;
		background: var(--color-surface);
	}
	.comparison {
		margin-top: 4rem;
	}
	.comparison figcaption {
		max-width: var(--container-measure);
		margin-top: 1rem;
		font-family: var(--font-mono);
		font-size: var(--text-mono-s);
		color: var(--color-ink-400);
	}
	:global(.article > .rule) {
		margin-block: var(--spacing-section);
	}
	.workflow,
	.fieldnote,
	.distinctions,
	.answer {
		display: grid;
		grid-template-columns: minmax(15rem, 0.75fr) minmax(0, 1.5fr);
		gap: clamp(2rem, 7vw, 7rem);
		padding-block: clamp(4rem, 8vw, 7rem);
		border-top: 1px solid var(--color-line);
	}
	.fieldnote {
		padding-bottom: 0;
	}
	.fieldnote-copy p {
		max-width: var(--container-measure);
	}
	.fieldnote-copy p + p {
		margin-top: 1.25rem;
	}
	.screenshot {
		margin-top: clamp(2.5rem, 5vw, 4rem);
	}
	.screenshot img {
		display: block;
		width: 100%;
		height: auto;
		border: 1px solid var(--color-line-strong);
		border-radius: var(--radius-win);
		box-shadow: var(--shadow-card);
	}
	.screenshot figcaption {
		max-width: var(--container-measure);
		margin-top: 1rem;
		font-family: var(--font-mono);
		font-size: var(--text-mono-s);
		color: var(--color-ink-400);
	}
	.shot-readouts {
		display: grid;
		grid-template-columns: repeat(3, minmax(0, 1fr));
		gap: 1px;
		margin-top: 2rem;
		border: 1px solid var(--color-line-strong);
		background: var(--color-line-strong);
	}
	.shot-readouts :global(.ro) {
		min-height: 6rem;
		padding: 1rem;
		background: var(--color-surface);
	}
	.workflow {
		border-top: 0;
		padding-top: 0;
	}
	.section-intro h2,
	.answer h2,
	.closing h2 {
		margin-top: 0.75rem;
		font-size: var(--text-h2);
		line-height: var(--text-h2--line-height);
		letter-spacing: var(--text-h2--letter-spacing);
	}
	.section-intro p {
		margin-top: 1.25rem;
		max-width: 34rem;
	}
	.steps {
		list-style: none;
	}
	.steps li {
		display: grid;
		grid-template-columns: 3rem 1fr;
		gap: 1rem;
		padding-block: 1.5rem;
		border-top: 1px solid var(--color-line);
	}
	.steps li:first-child {
		padding-top: 0;
		border-top: 0;
	}
	.step-index {
		font-family: var(--font-mono);
		font-size: var(--text-mono-s);
		color: var(--color-cut-700);
	}
	.steps h3,
	.cards h3 {
		font-size: var(--text-h3);
		line-height: var(--text-h3--line-height);
	}
	.steps p,
	.cards p,
	.answer-copy p {
		margin-top: 0.65rem;
		max-width: var(--container-measure);
	}
	.cards {
		display: grid;
		gap: 1rem;
	}
	.cards article {
		padding: 1.5rem;
		border: 1px solid var(--color-line-strong);
		border-radius: var(--radius-md);
		background: var(--color-surface);
		box-shadow: var(--shadow-card);
	}
	.answer-copy p:first-child {
		margin-top: 0;
	}
	.shelf {
		grid-column: 1 / -1;
	}
	.closing {
		margin-top: clamp(4rem, 8vw, 7rem);
		padding: clamp(2rem, 6vw, 5rem);
		border-radius: var(--radius-win);
		background: var(--color-panel);
		border: 1px solid var(--color-line-strong);
	}
	.closing p {
		max-width: var(--container-measure);
		margin-top: 1.25rem;
	}
	@media (max-width: 760px) {
		:global(.article) {
			padding-top: 3rem;
		}
		.evidence,
		.shot-readouts,
		.workflow,
		.fieldnote,
		.distinctions,
		.answer {
			grid-template-columns: 1fr;
		}
		.workflow,
		.fieldnote,
		.distinctions,
		.answer {
			gap: 2rem;
		}
	}
</style>
